import React from 'react';
import { TextField, InputAdornment, CircularProgress } from '@mui/material';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { COLOR } from '@src/styles/color';
import { StyledUrlLoaderWrap } from './index.style';

/**
 * URL input atom — MUI TextField with LinkRounded start icon and loading spinner end icon.
 *
 * @param {string} value — current URL string.
 * @param {(next: string) => void} onChange — called with the next value.
 * @param {boolean} [loading=false] — shows an end-adornment spinner when true.
 * @param {boolean} [disabled=false] — disables the input.
 * @param {boolean} [error=false] — red border + helper text when true.
 * @param {string} [errorText] — helper text below the field (used with `error`).
 * @param {string} [placeholder] — input placeholder; consumer usually passes `t('exampleValidUrl')`.
 */
const UrlInputField = ({
  value,
  onChange,
  loading = false,
  disabled = false,
  error = false,
  errorText,
  placeholder,
}) => (
  <TextField
    fullWidth
    value={value ?? ''}
    onChange={(e) => onChange?.(e.target.value)}
    placeholder={placeholder}
    error={error}
    helperText={error ? errorText : ''}
    disabled={disabled}
    sx={{
      '& .MuiOutlinedInput-root': {
        backgroundColor: COLOR.white,
        borderRadius: '8px',
        height: '40px',
        padding: '8px 12px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '0.5px',
        borderColor: COLOR.indigo[16],
      },
      '&:hover .MuiOutlinedInput-notchedOutline, & .Mui-focused .MuiOutlinedInput-notchedOutline':
        {
          borderWidth: '0.5px',
          borderColor: COLOR.indigo[32],
        },
      '& .MuiOutlinedInput-input': {
        padding: 0,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '-0.4px',
        color: COLOR.darkGray,
        '&::placeholder': {
          color: COLOR.label2[64],
          opacity: 1,
        },
      },
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <LinkRoundedIcon
            sx={{ width: 24, height: 24, color: COLOR.label2[64] }}
          />
        </InputAdornment>
      ),
      endAdornment: loading ? (
        <InputAdornment position="end">
          <StyledUrlLoaderWrap>
            <CircularProgress
              size={20}
              thickness={5}
              sx={{ color: COLOR.indigo[64] }}
            />
          </StyledUrlLoaderWrap>
        </InputAdornment>
      ) : null,
    }}
  />
);

export default UrlInputField;
